import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import {
    browserIdentityStorageKey,
    createBrowserIdentity,
    createIdentityId,
    getOrCreateStoredBrowserId,
    isIdentityId,
    isTrackedPublicPath,
    resolveSessionChannel,
    takeCampaignCodeFromLocation
} from './telemetry-identity';

const sessionIdentityStorageKey = 'digitaljedwin.telemetry.session';

export type SiteTelemetry = {
    start: (initialPath: string) => void;
    trackPageView: (path: string) => void;
    /** The IDs every public-site telemetry record carries, so other trackers share this one identity source. */
    getIdentity: () => { browserId: string; sessionId: string };
    /** This session's channel (for example "pinterest" or "qr:k3x9p2"), sent with a checkout instead of any ID. */
    getChannel: () => string;
    /** For the other channel, the referring site's host (for example "example-blog.com"), sent with a checkout. */
    getReferrerHost: () => string | null;
};

export function createSiteTelemetry(): SiteTelemetry {
    // Runs before the gallery reads the address, so a /q/ redirect's #c= fragment is gone before anything sees it.
    const arrivingCampaignCode = takeCampaignCodeFromLocation(window.location, window.history);
    const browserId = getOrCreateStoredBrowserId() ?? createIdentityId();
    const sessionId = getOrCreateSessionId();
    const sessionChannel = resolveSessionChannel({
        sessionId,
        campaignCode: arrivingCampaignCode,
        referrer: document.referrer,
        hostname: window.location.hostname
    });
    let connection: ReturnType<HubConnectionBuilder['build']> | null = null;
    let lastTrackedPath: string | null = null;
    let currentPath: string | null = null;
    let homepageSessionStartedAt: number | null = null;
    let includeDocumentReferrer = true;
    let retryTimeoutId: number | null = null;

    function startConnection(): void {
        if (connection == null || connection.state !== HubConnectionState.Disconnected) return;
        void connection.start()
            .catch(() => {
                if (retryTimeoutId != null) return;
                retryTimeoutId = window.setTimeout(() => {
                    retryTimeoutId = null;
                    startConnection();
                }, 10_000);
            });
    }

    function trackPageView(path: string): void {
        if (!isTrackedPublicPath(path)) return;
        renewBrowserIdentity(browserId);
        updateHomepageSession(path);
        if (path === lastTrackedPath) return;
        lastTrackedPath = path;
        const referrer = includeDocumentReferrer ? document.referrer : '';
        includeDocumentReferrer = false;
        void fetch('/api/telemetry/page-view', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            keepalive: true,
            body: JSON.stringify({ browserId, sessionId, path, referrer })
        }).catch(() => {
            // Telemetry must never interfere with the public site.
        });
    }

    function updateHomepageSession(path: string): void {
        if (currentPath === '/' && path !== '/') endHomepageSession();
        currentPath = path;
        if (path === '/' && !document.hidden) startHomepageSession();
    }

    function startHomepageSession(): void {
        homepageSessionStartedAt ??= performance.now();
    }

    function endHomepageSession(): void {
        if (homepageSessionStartedAt == null) return;
        const durationMilliseconds = performance.now() - homepageSessionStartedAt;
        homepageSessionStartedAt = null;
        if (durationMilliseconds <= 0) return;
        void fetch('/api/telemetry/homepage-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            keepalive: true,
            body: JSON.stringify({ browserId, durationMilliseconds })
        }).catch(() => {
            // Telemetry must never interfere with the public site.
        });
    }

    window.addEventListener('online', startConnection);
    window.addEventListener('pagehide', endHomepageSession);
    window.addEventListener('pageshow', () => {
        if (currentPath === '/' && !document.hidden) startHomepageSession();
    });
    document.addEventListener('visibilitychange', () => {
        if (currentPath !== '/') return;
        if (document.hidden) endHomepageSession();
        else startHomepageSession();
    });

    return {
        start(initialPath: string): void {
            if (!isTrackedPublicPath(initialPath)) return;
            const query = new URLSearchParams({
                browserId,
                sessionId,
                landingPath: initialPath,
                referrer: document.referrer
            });
            // The server classifies the session as qr:<code> when the code belongs to an active campaign link.
            if (sessionChannel.campaignCode != null) query.set('campaign', sessionChannel.campaignCode);
            connection = new HubConnectionBuilder()
                .withUrl(`/hubs/telemetry-presence?${query.toString()}`)
                .withAutomaticReconnect([0, 2_000, 10_000, 30_000])
                .configureLogging(LogLevel.None)
                .build();
            startConnection();
            trackPageView(initialPath);
        },
        trackPageView,
        getIdentity: () => ({ browserId, sessionId }),
        getChannel: () => sessionChannel.channel,
        getReferrerHost: () => sessionChannel.referrerHost
    };
}

function renewBrowserIdentity(id: string, now = Date.now()): void {
    try {
        window.localStorage.setItem(browserIdentityStorageKey, JSON.stringify(createBrowserIdentity(now, id)));
    } catch {
        // Storage can be unavailable without disabling telemetry for the current document.
    }
}

function getOrCreateSessionId(): string {
    try {
        const stored = window.sessionStorage.getItem(sessionIdentityStorageKey);
        if (stored != null && isIdentityId(stored)) return stored;
        const sessionId = createIdentityId();
        window.sessionStorage.setItem(sessionIdentityStorageKey, sessionId);
        return sessionId;
    } catch {
        return createIdentityId();
    }
}
